import { Download, Copy, Send, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CHIP = { sent: "text-amber-300", signed: "text-emerald-300", voided: "text-slate-500" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function AgreementsList({ items, onChanged }) {
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="agreements-empty">No agreements sent yet.</p>;

  const download = async (a) => {
    try {
      const r = await api.get(`/admin/agreements/${a.agreement_id}/pdf`, { responseType: "blob", timeout: 60000 });
      saveBlob(r.data, `${a.agreement_id}-${a.status === "signed" ? "signed" : "draft"}.pdf`);
    } catch { toast.error("Download failed"); }
  };
  const resend = async (a) => {
    try { const r = await api.post(`/admin/agreements/${a.agreement_id}/resend`); toast.success(r.data.email_sent ? "Re-sent" : "Email didn't go out — copy the link instead"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't re-send"); }
  };
  const voidIt = async (a) => {
    if (!window.confirm(`Withdraw the unsigned agreement for ${a.tech_name}? The link will stop working.`)) return;
    try { await api.post(`/admin/agreements/${a.agreement_id}/void`); toast.success("Withdrawn"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't withdraw"); }
  };

  return (
    <ul className="divide-y divide-slate-800" data-testid="agreements-list">
      {items.map((a) => (
        <li key={a.agreement_id} className="py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`agreement-row-${a.agreement_id}`}>
          <span className={`dg-chip ${CHIP[a.status] || ""}`} data-testid={`agreement-status-${a.agreement_id}`}>{a.status}</span>
          <span className="font-medium text-slate-100">{a.signed_name || a.tech_name}</span>
          <span className="text-slate-400 truncate">{a.tech_email}</span>
          <span className="text-xs text-slate-500 font-mono">{a.agreement_id}</span>
          <span className="text-xs text-slate-500">{a.status === "signed" ? `signed ${fmt(a.signed_at)}` : a.status === "voided" ? `withdrawn ${fmt(a.voided_at)}` : a.viewed_at ? `opened ${fmt(a.viewed_at)}` : `sent ${fmt(a.sent_at)}${a.email_sent === false ? " · email failed" : ""}`}</span>
          <span className="ml-auto flex items-center gap-3">
            <button type="button" onClick={() => download(a)} className="dg-link text-xs inline-flex items-center gap-1" data-testid={`agreement-pdf-${a.agreement_id}`}><Download className="w-3 h-3" /> PDF</button>
            {a.status === "sent" && <>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(a.sign_url); toast.success("Link copied"); }} className="dg-link text-xs inline-flex items-center gap-1" data-testid={`agreement-copy-${a.agreement_id}`}><Copy className="w-3 h-3" /> link</button>
              <button type="button" onClick={() => resend(a)} className="dg-link text-xs inline-flex items-center gap-1" data-testid={`agreement-resend-${a.agreement_id}`}><Send className="w-3 h-3" /> resend</button>
              <button type="button" onClick={() => voidIt(a)} className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1" data-testid={`agreement-void-${a.agreement_id}`}><Ban className="w-3 h-3" /> withdraw</button>
            </>}
          </span>
        </li>
      ))}
    </ul>
  );
}
